/**
 * 原生窗口能力（桌面端专用）：吸附窗口 + 主窗体宽度。
 *
 * 页面通过本地服务的 `/__xts/window` 触发，而不是走 Tauri IPC：页面是从
 * `http://127.0.0.1:<port>` 加载的，走 IPC 需要在 capability 里给远端 origin 授权
 * （`remote.urls`），走本地端点就不必动那套配置（见 docs/DESKTOP.md §十）。
 *
 * 网页端没有本地服务，调用会失败 —— 调用方一律先用 `isDesktopHost()` 判断。
 *
 * 注：**"面板展开时加宽主窗口"那套机制已按用户要求移除**（两条动画曲线不同步，
 * 展开瞬间编辑区与面板会互相挤压）。面板改为在窗口内从右侧向左伸出，窗口宽度不变。
 */

async function call(body: unknown): Promise<Record<string, unknown> | null> {
  const result = await callRaw(body);
  return result.ok ? result.data : null;
}

/**
 * 与 `call` 相同，但**不吞错误**：把 HTTP 状态与服务端给的错误文案一起带回来。
 *
 * 为什么需要它：老板键的失败（托盘没建出来、钩子没装上、窗口操作被系统拒绝）以前表现为
 * "按了完全没反应" —— 因为 `call` 把 `!res.ok` 直接变成 `null`。设置页的「试一下」按钮
 * 就是靠这个把原因显示给用户。
 */
export async function callRaw(body: unknown): Promise<{
  ok: boolean;
  status: number;
  data: Record<string, unknown> | null;
  error: string | null;
}> {
  try {
    const res = await fetch('/__xts/window', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data: Record<string, unknown> | null = null;
    try {
      data = (await res.json()) as Record<string, unknown>;
    } catch {
      data = null;
    }
    const message = typeof data?.error === 'string' ? data.error : null;
    return { ok: res.ok, status: res.status, data, error: res.ok ? null : message ?? `HTTP ${res.status}` };
  } catch (err) {
    // 本地服务刚好不可用（正在退出等）
    return { ok: false, status: 0, data: null, error: err instanceof Error ? err.message : '本地服务不可用' };
  }
}

export async function openPaneWindow(
  pane: string,
  onLeft = false,
  width?: number,
): Promise<'opened' | 'switched' | 'focused' | null> {
  const data = await call({ action: 'open-pane', pane, side: onLeft ? 'left' : 'right', width });
  const result = data?.result;
  return result === 'opened' || result === 'switched' || result === 'focused' ? result : null;
}

/**
 * 让吸附窗口贴回主窗口（切换左右布局时调用；没开窗则什么都不做）。
 *
 * `onLeft = true` 把它挪到主窗口**左侧**。
 */
export async function dockSatellite(onLeft: boolean): Promise<void> {
  await call({ action: 'dock', side: onLeft ? 'left' : 'right' });
}



/** 关掉某页的独立窗口（"收回"；没开也不报错 —— 用户可能已经在别处关掉了）。 */
export async function closePaneWindow(pane: string): Promise<void> {
  await call({ action: 'close-pane', pane });
}

/**
 * 现在开着独立窗口的页有哪些。
 *
 * 主窗口（重新）加载时问一次 —— 这样"刷新过主窗口、但独立窗口还开着"时，
 * 图标栏上的按钮仍然是"不可点"的正确状态。
 */
export async function listPaneWindows(): Promise<string[]> {
  const data = await call({ action: 'list-panes' });
  const panes = data?.panes;
  return Array.isArray(panes) ? panes.filter((p): p is string => typeof p === 'string') : [];
}
/**
 * 老板键：收起或恢复整个应用（由 Rust 判断当前该收还是该放，页面侧无状态）。
 *
 * 返回动作之后是否隐藏，以及**失败原因**（设置页的「试一下」按钮要把原因显示出来 ——
 * 这类失败以前是静默的，用户只看到"按了没反应"）。
 */
export async function bossToggle(): Promise<{ hidden: boolean | null; error: string | null }> {
  const res = await callRaw({ action: 'boss-toggle' });
  if (!res.ok) return { hidden: null, error: res.error };
  const hidden = res.data?.hidden;
  return { hidden: typeof hidden === 'boolean' ? hidden : null, error: null };
}

/** 老板键的运行状态（托盘有没有建出来 / 键盘钩子有没有装上 / 当前组合是什么） */
export interface BossStatus {
  trayOk: boolean;
  hookOk: boolean;
  /** 已下发给钩子的组合（虚拟键码） */
  keys: number[];
  /** 钩子**实际看到**的最近按键（空 = 它一个按键都没收到，那多半是权限/前台程序问题） */
  lastKeys: number[];
  /** 钩子看到过的按键总数（只增不减 —— 用它判断"按键到底进没进钩子"） */
  keyEvents: number;
  /** 上一次命中组合 + 动作结果（区分"没命中"与"命中了但收起失败"） */
  lastHit: string | null;
  visible: boolean;
}

export async function fetchBossStatus(): Promise<BossStatus | null> {
  const data = await call({ action: 'boss-status' });
  if (!data) return null;
  return {
    trayOk: data.trayOk === true,
    hookOk: data.hookOk === true,
    keys: Array.isArray(data.keys) ? (data.keys as number[]) : [],
    lastKeys: Array.isArray(data.lastKeys) ? (data.lastKeys as number[]) : [],
    keyEvents: typeof data.keyEvents === 'number' ? data.keyEvents : 0,
    lastHit: typeof data.lastHit === 'string' ? data.lastHit : null,
    visible: data.visible !== false,
  };
}

/**
 * 把老板键的组合下发给宿主（虚拟键码数组；空数组 = 关闭老板键）。
 *
 * 识别在 Rust 的系统级键盘钩子上（见 src-tauri/src/bosskey.rs）——
 * 窗口收起后页面收不到键盘事件，只有钩子能让"同一个快捷键收起+恢复"成立。
 */
export async function setBossKeysOnHost(keys: number[]): Promise<number[] | null> {
  const data = await call({ action: 'boss-set', keys });
  const stored = data?.keys;
  return Array.isArray(stored) ? stored.filter((k): k is number => typeof k === 'number') : null;
}

/**
 * 把主窗口宽度设成 `width`（CSS 像素）。
 *
 * 两个用途（用户约定）：**首次启动**应用算出来的默认宽度；设置页点「重置页面宽度」时重设。
 * Rust 侧会乘缩放比换算成物理像素，并保证不小于最小宽度。
 */
export async function sizeMainWindow(width: number): Promise<number | null> {
  const rounded = Math.round(width);
  if (!Number.isFinite(rounded) || rounded <= 0) return null;
  const data = await call({ action: 'size-main', width: rounded });
  const result = data?.width;
  return typeof result === 'number' ? result : null;
}

// ── 自更新（一个 exe 完成：下载 → 校验 → 自替换）─────────────────────────

/** 后端 `GET /api/desktop/latest` 的清单（客户端据此比对版本、校验下载） */
export interface DesktopRelease {
  version: string;
  file: string;
  size?: number;
  sha256: string;
  releasedAt?: string;
  notes?: string;
}

/** 下载/替换状态（与 Rust 侧 `updater::UpdateState` 的 JSON 对应） */
export type UpdateState =
  | { state: 'idle' }
  | { state: 'downloading'; version: string; received: number; total: number; percent: number }
  | { state: 'ready'; version: string }
  | { state: 'failed'; message: string };

/**
 * 拉最新版本清单。
 *
 * 走既有的 `/api` 反向代理（和所有业务请求同一条路）—— 所以"客户端连哪台服务器，
 * 就从哪台查更新"。返回 `null` 表示服务器上还没有发布过桌面端版本（不是错误）。
 */
export async function fetchLatestRelease(): Promise<DesktopRelease | null> {
  try {
    const res = await fetch('/api/desktop/latest', { credentials: 'include' });
    if (res.status === 404) return null;
    if (!res.ok) return null;
    const data = await res.json() as DesktopRelease;
    return data?.version && data?.sha256 ? data : null;
  } catch {
    return null;
  }
}

/** 版本比较：`candidate` 是否比 `current` 新（点分数字，容忍 v 前缀） */
export function isNewerVersion(candidate: string, current: string): boolean {
  const parts = (v: string) => v.trim().replace(/^v/, '').split(/[.\-+]/).map(p => Number.parseInt(p, 10) || 0);
  const a = parts(candidate);
  const b = parts(current);
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x > y;
  }
  return false;
}

/** 让宿主开始后台下载新版本（幂等） */
export async function startUpdateDownload(): Promise<UpdateState | null> {
  const data = await call({ action: 'update-download' });
  return (data as UpdateState | null) ?? null;
}

/** 主窗口是否最大化 / 全屏（数据页据此决定放窗口内还是吸附窗口） */
export interface WindowExpandedState {
  maximized: boolean;
  fullscreen: boolean;
  /** 两者之一 —— 用它就够了（"用满屏幕"时不该再弹副窗口） */
  expanded: boolean;
}

/**
 * 查主窗口的"是否最大化 / 全屏"。
 *
 * 判定在 Rust 侧（`is_maximized()` / `is_fullscreen()`）：那是真实窗口状态，
 * 比前端拿 `innerWidth` 与工作区比对可靠（最大化窗口的内尺寸还差着边框宽度）。
 */
export async function fetchWindowExpanded(): Promise<WindowExpandedState | null> {
  const data = await call({ action: 'window-state' });
  if (!data) return null;
  return {
    maximized: data.maximized === true,
    fullscreen: data.fullscreen === true,
    expanded: data.expanded === true,
  };
}

/** 查下载进度 */
export async function fetchUpdateState(): Promise<UpdateState | null> {
  const data = await call({ action: 'update-status' });
  return (data as UpdateState | null) ?? null;
}

/**
 * 应用更新：宿主会启动新版本、由它替换当前 exe，随后**本进程退出**。
 * 所以调用之后页面不该再做任何事（通常几百毫秒后窗口就没了）。
 */
export async function applyUpdate(): Promise<{ restarting: boolean; error?: string }> {
  try {
    const res = await callRaw({ action: 'update-apply' });
    if (!res.ok) return { restarting: false, error: res.error ?? '应用更新失败' };
    return { restarting: true };
  } catch (error) {
    return { restarting: false, error: (error as Error).message };
  }
}